import { useI18N } from "@sundae/react-hooks";
import { Button, Dialog } from "@sundae/ui-toolkit";
import { IDialogProps } from "@sundae/ui-toolkit/dist/types/components/organisms/Dialog/Dialog";
import { Fraction } from "@sundaeswap/fraction";
import cx from "classnames";
import { AnimatePresence } from "framer-motion";
import { FC, Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { SundaeUtils } from "@sundaeswap/core/utilities";
import { testIds } from "../../../constants/testIds.constants";
import { useUpdatePositionMutation } from "../../../hooks/useUpdatePositionTx";
import { ETransactionState } from "../../../types/Transaction.types";
import { TAssetIdPoolMap } from "../../../utils/pool.utils";
import LoadingComponent from "../../LoadingComponent";
import Motion from "../../Motion/Motion";
import {
  MIN_ADA_LIMIT,
  useYieldFarmingPositionDialogContext,
} from "../../YieldFarmingComponents/PositionTable/context";
import PositionValueList from "../../YieldFarmingComponents/PositionValueList";

const LazySubmittingUpdatePosition = lazy(
  () =>
    import(
      /* webpackChunkName: "SubmittingUpdatePosition" */ "../../../i18n/components/farms/SubmittingUpdatePosition"
    )
);

const LazyOrderError = lazy(
  () => import(/* webpackChunkName: "OrderError" */ "../../OrderComponents/OrderError")
);

const LazyOrderSuccess = lazy(
  () => import(/* webpackChunkName: "OrderSuccess" */ "../../OrderComponents/OrderSuccess")
);

interface IUpdateYieldFarmingPositionDialogProps extends IDialogProps {
  lpAssetsPoolMap: TAssetIdPoolMap;
  onMutationSuccess: (txHash: string) => void;
  isLoading: boolean;
}

const UpdateYieldFarmingPositionDialog: FC<IUpdateYieldFarmingPositionDialogProps> = ({
  closeDialog,
  lpAssetsPoolMap,
  onOpenChange,
  open,
  onMutationSuccess,
  isLoading,
}) => {
  const { t } = useI18N("farms");
  const [isInputBelowAdaMinLimit, setIsInputBelowAdaMinLimit] = useState(false);
  const { dialogState, insufficientAdaBalance, positionValues, resetContext, setDialogState } =
    useYieldFarmingPositionDialogContext();

  const { handleSubmitUpdatePositionTransaction } = useUpdatePositionMutation({
    enabled: !isInputBelowAdaMinLimit,
    onMutationSuccess,
  });

  const handleUpdatePositionTx = useCallback(async () => {
    setDialogState(ETransactionState.submitting);
    await handleSubmitUpdatePositionTransaction.mutateAsync();
  }, []);

  const isError = dialogState === ETransactionState.error;
  const isSuccess = dialogState === ETransactionState.success;
  const isSubmitting = dialogState === ETransactionState.submitting;

  /**
   * Memoized variable that returns whether the `Update Positions` button should be disabled or not.
   * The button should be disabled if the ADA value is less than the limit (5 ADA) or all locked assets are 0.
   */
  const isActionButtonDisabled = useMemo(() => {
    let hasSufficientAdaLocked = false;
    let hasOtherTokensLocked = false;
    positionValues?.value?.forEach(({ assetAmount, asset }) => {
      if (
        SundaeUtils.isAdaAsset(asset.metadata) &&
        assetAmount.amount >= MIN_ADA_LIMIT.amount &&
        !hasSufficientAdaLocked
      ) {
        hasSufficientAdaLocked = true;
      }

      if (!SundaeUtils.isAdaAsset(asset.metadata) && !hasOtherTokensLocked) {
        hasOtherTokensLocked = true;
      }
    });

    if (!hasOtherTokensLocked || (!hasSufficientAdaLocked && insufficientAdaBalance)) {
      return true;
    }

    return positionValues?.value.some(({ asset, assetAmount }) => {
      const isAda = SundaeUtils.isAdaAsset(asset.metadata);
      const isOneOfAssetsZero = positionValues.value.some(({ assetAmount }) =>
        assetAmount.value.equals(Fraction.ZERO)
      );
      const isOneOfAssetsAboveBalance = positionValues.value.some(
        ({ assetAmount, totalAssetAmount }) => {
          return assetAmount.value.greaterThan(totalAssetAmount.value);
        }
      );

      if (isAda) {
        const isAdaInputLessThanLimit = assetAmount.amount < MIN_ADA_LIMIT.amount;
        setIsInputBelowAdaMinLimit(isAdaInputLessThanLimit);
        return isAdaInputLessThanLimit;
      }

      return isOneOfAssetsZero || isOneOfAssetsAboveBalance;
    });
  }, [insufficientAdaBalance, positionValues]);

  const renderContent = useCallback(() => {
    if (isError) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyOrderError />
        </Suspense>
      );
    }

    if (isSuccess) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyOrderSuccess
            className="mb-4"
            onCancel={closeDialog}
            onSuccess={closeDialog}
            onSuccessButtonText={t("close", { ns: "common" })}
          />
        </Suspense>
      );
    }

    if (isSubmitting) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazySubmittingUpdatePosition />
        </Suspense>
      );
    }

    return (
      <Motion className="grid gap-4">
        <PositionValueList
          isInputBelowAdaMinLimit={isInputBelowAdaMinLimit}
          lpAssetsPoolMap={lpAssetsPoolMap || new Map()}
          isLoading={isLoading}
        />

        {/* -------------------------------------------------------------------------------------------------
         * Action Buttons
         * -----------------------------------------------------------------------------------------------*/}
        <div className="grid w-full gap-2 pl-[22px] lg:flex lg:items-center">
          <Button
            className="flex-1"
            disabled={isActionButtonDisabled}
            variant="primary"
            onClick={handleUpdatePositionTx}
          >
            {t("portfolio.positions.action")}
          </Button>
        </div>
      </Motion>
    );
  }, [
    isActionButtonDisabled,
    handleUpdatePositionTx,
    isInputBelowAdaMinLimit,
    isError,
    isSubmitting,
  ]);

  /** Reset Dialog on unmount */
  useEffect(() => {
    return () => {
      resetContext();
    };
  }, []);

  return (
    <Dialog
      description={t("portfolio.positions.description", {
        context: "dialog",
      })}
      open={open}
      onOpenChange={onOpenChange}
      closeDialog={closeDialog}
      title={t("portfolio.positions.action")}
      cardContainerClassName="lg:!w-[40rem]"
    >
      <div
        className={cx("grid min-h-[18rem] gap-4 xl:gap-5", {
          "content-start": !isSuccess && !isSubmitting,
        })}
        data-testid={testIds.yieldFarming.positions.dialogs.edit}
      >
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </div>
    </Dialog>
  );
};

export default UpdateYieldFarmingPositionDialog;
